// BEA Benchmark IO detail-industry codes — the `industry_code` axis the MRIO
// impact API is keyed on. Each BEA code maps 1:1 to an industry title; the
// scenario request sends the code and the response echoes it, which beaLabel
// resolves back to the title for the charts. `naics` is the BEA→NAICS
// crosswalk (one BEA code aggregates one or more NAICS, 2–6 digit) used to make
// the industry picker searchable by NAICS code; some aggregates (owner-occupied
// housing, government) have no NAICS. Sourced from the public-domain BEA/Census
// BEA<->NAICS crosswalk (396 industries).

export interface BeaIndustry {
  bea: string;
  label: string;
  /** NAICS codes this BEA detail industry aggregates (2–6 digit; may be empty). */
  naics: string[];
}

export const BEA_INDUSTRIES: BeaIndustry[] = [
  { bea: '1111A0', label: 'Oilseed farming', naics: ['11111', '11112'] },
  {
    bea: '1111B0',
    label: 'Grain farming',
    naics: ['11113', '11114', '11115', '11116', '11119'],
  },
  { bea: '111200', label: 'Vegetable and melon farming', naics: ['1112'] },
  { bea: '111300', label: 'Fruit and tree nut farming', naics: ['1113'] },
  {
    bea: '111400',
    label: 'Greenhouse, nursery, and floriculture production',
    naics: ['1114'],
  },
  { bea: '111900', label: 'Other crop farming', naics: ['1119'] },
  {
    bea: '112120',
    label: 'Dairy cattle and milk production',
    naics: ['11212'],
  },
  {
    bea: '1121A0',
    label:
      'Beef cattle ranching and farming, including feedlots and dual-purpose ranching and farming',
    naics: ['11211', '11213'],
  },
  { bea: '112300', label: 'Poultry and egg production', naics: ['1123'] },
  {
    bea: '112A00',
    label: 'Animal production, except cattle and poultry and eggs',
    naics: ['1122', '1124', '1125', '1129'],
  },
  { bea: '113000', label: 'Forestry and logging', naics: ['113'] },
  { bea: '114000', label: 'Fishing, hunting and trapping', naics: ['114'] },
  {
    bea: '115000',
    label: 'Support activities for agriculture and forestry',
    naics: ['115'],
  },
  { bea: '211000', label: 'Oil and gas extraction', naics: ['211'] },
  { bea: '212100', label: 'Coal mining', naics: ['2121'] },
  {
    bea: '212230',
    label: 'Copper, nickel, lead, and zinc mining',
    naics: ['21223'],
  },
  {
    bea: '2122A0',
    label: 'Iron, gold, silver, and other metal ore mining',
    naics: ['21221', '21222', '21229'],
  },
  { bea: '212310', label: 'Stone mining and quarrying', naics: ['21231'] },
  {
    bea: '2123A0',
    label: 'Other nonmetallic mineral mining and quarrying',
    naics: ['21232', '21239'],
  },
  { bea: '213111', label: 'Drilling oil and gas wells', naics: ['213111'] },
  {
    bea: '21311A',
    label: 'Other support activities for mining',
    naics: ['213112', '213113', '213114', '213115'],
  },
  {
    bea: '221100',
    label: 'Electric power generation, transmission, and distribution',
    naics: ['2211'],
  },
  { bea: '221200', label: 'Natural gas distribution', naics: ['2212'] },
  { bea: '221300', label: 'Water, sewage and other systems', naics: ['2213'] },
  {
    bea: '230301',
    label: 'Nonresidential maintenance and repair',
    naics: ['23'],
  },
  { bea: '230302', label: 'Residential maintenance and repair', naics: ['23'] },
  { bea: '233210', label: 'Health care structures', naics: ['23'] },
  { bea: '233230', label: 'Manufacturing structures', naics: ['23'] },
  { bea: '233240', label: 'Power and communication structures', naics: ['23'] },
  {
    bea: '233262',
    label: 'Educational and vocational structures',
    naics: ['23'],
  },
  { bea: '2332A0', label: 'Office and commercial structures', naics: ['23'] },
  {
    bea: '2332C0',
    label: 'Transportation structures and highways and streets',
    naics: ['23'],
  },
  { bea: '2332D0', label: 'Other nonresidential structures', naics: ['23'] },
  {
    bea: '233411',
    label: 'Single-family residential structures',
    naics: ['23'],
  },
  { bea: '233412', label: 'Multifamily residential structures', naics: ['23'] },
  { bea: '2334A0', label: 'Other residential structures', naics: ['23'] },
  { bea: '311111', label: 'Dog and cat food manufacturing', naics: ['311111'] },
  {
    bea: '311119',
    label: 'Other animal food manufacturing',
    naics: ['311119'],
  },
  {
    bea: '311210',
    label: 'Flour milling and malt manufacturing',
    naics: ['31121'],
  },
  { bea: '311221', label: 'Wet corn milling', naics: ['311221'] },
  {
    bea: '311224',
    label: 'Soybean and other oilseed processing',
    naics: ['311224'],
  },
  {
    bea: '311225',
    label: 'Fats and oils refining and blending',
    naics: ['311225'],
  },
  { bea: '311230', label: 'Breakfast cereal manufacturing', naics: ['31123'] },
  {
    bea: '311300',
    label: 'Sugar and confectionery product manufacturing',
    naics: ['3113'],
  },
  { bea: '311410', label: 'Frozen food manufacturing', naics: ['31141'] },
  {
    bea: '311420',
    label: 'Fruit and vegetable canning, pickling, and drying',
    naics: ['31142'],
  },
  { bea: '311513', label: 'Cheese manufacturing', naics: ['311513'] },
  {
    bea: '311514',
    label: 'Dry, condensed, and evaporated dairy product manufacturing',
    naics: ['311514'],
  },
  {
    bea: '31151A',
    label: 'Fluid milk and butter manufacturing',
    naics: ['311511', '311512'],
  },
  {
    bea: '311520',
    label: 'Ice cream and frozen dessert manufacturing',
    naics: ['31152'],
  },
  { bea: '311615', label: 'Poultry processing', naics: ['311615'] },
  {
    bea: '31161A',
    label: 'Animal (except poultry) slaughtering, rendering, and processing',
    naics: ['311611', '311612', '311613'],
  },
  {
    bea: '311700',
    label: 'Seafood product preparation and packaging',
    naics: ['3117'],
  },
  {
    bea: '311810',
    label: 'Bread and bakery product manufacturing',
    naics: ['31181'],
  },
  {
    bea: '3118A0',
    label: 'Cookie, cracker, pasta, and tortilla manufacturing',
    naics: ['31182', '31183'],
  },
  { bea: '311910', label: 'Snack food manufacturing', naics: ['31191'] },
  { bea: '311920', label: 'Coffee and tea manufacturing', naics: ['31192'] },
  {
    bea: '311930',
    label: 'Flavoring syrup and concentrate manufacturing',
    naics: ['31193'],
  },
  {
    bea: '311940',
    label: 'Seasoning and dressing manufacturing',
    naics: ['31194'],
  },
  { bea: '311990', label: 'All other food manufacturing', naics: ['31199'] },
  {
    bea: '312110',
    label: 'Soft drink and ice manufacturing',
    naics: ['31211'],
  },
  { bea: '312120', label: 'Breweries', naics: ['31212'] },
  { bea: '312130', label: 'Wineries', naics: ['31213'] },
  { bea: '312140', label: 'Distilleries', naics: ['31214'] },
  { bea: '312200', label: 'Tobacco manufacturing', naics: ['3122'] },
  { bea: '313100', label: 'Fiber, yarn, and thread mills', naics: ['3131'] },
  { bea: '313200', label: 'Fabric mills', naics: ['3132'] },
  {
    bea: '313300',
    label: 'Textile and fabric finishing and fabric coating mills',
    naics: ['3133'],
  },
  { bea: '314110', label: 'Carpet and rug mills', naics: ['31411'] },
  { bea: '314120', label: 'Curtain and linen mills', naics: ['31412'] },
  { bea: '314900', label: 'Other textile product mills', naics: ['3149'] },
  { bea: '315000', label: 'Apparel manufacturing', naics: ['315'] },
  {
    bea: '316000',
    label: 'Leather and allied product manufacturing',
    naics: ['316'],
  },
  { bea: '321100', label: 'Sawmills and wood preservation', naics: ['3211'] },
  {
    bea: '321200',
    label: 'Veneer, plywood, and engineered wood product manufacturing',
    naics: ['3212'],
  },
  { bea: '321910', label: 'Millwork', naics: ['32191'] },
  {
    bea: '3219A0',
    label: 'All other wood product manufacturing',
    naics: [
      '32192',
      '32193',
      '32194',
      '32195',
      '32196',
      '32197',
      '32198',
      '32199',
    ],
  },
  { bea: '322110', label: 'Pulp mills', naics: ['32211'] },
  { bea: '322120', label: 'Paper mills', naics: ['32212'] },
  { bea: '322130', label: 'Paperboard mills', naics: ['32213'] },
  {
    bea: '322210',
    label: 'Paperboard container manufacturing',
    naics: ['32221'],
  },
  {
    bea: '322220',
    label: 'Paper bag and coated and treated paper manufacturing',
    naics: ['32222'],
  },
  {
    bea: '322230',
    label: 'Stationery product manufacturing',
    naics: ['32223'],
  },
  {
    bea: '322291',
    label: 'Sanitary paper product manufacturing',
    naics: ['322291'],
  },
  {
    bea: '322299',
    label: 'All other converted paper product manufacturing',
    naics: ['322299'],
  },
  { bea: '323110', label: 'Printing', naics: ['32311'] },
  { bea: '323120', label: 'Support activities for printing', naics: ['32312'] },
  { bea: '324110', label: 'Petroleum refineries', naics: ['32411'] },
  {
    bea: '324121',
    label: 'Asphalt paving mixture and block manufacturing',
    naics: ['324121'],
  },
  {
    bea: '324122',
    label: 'Asphalt shingle and coating materials manufacturing',
    naics: ['324122'],
  },
  {
    bea: '324190',
    label: 'Other petroleum and coal products manufacturing',
    naics: ['32419'],
  },
  { bea: '325110', label: 'Petrochemical manufacturing', naics: ['32511'] },
  { bea: '325120', label: 'Industrial gas manufacturing', naics: ['32512'] },
  {
    bea: '325130',
    label: 'Synthetic dye and pigment manufacturing',
    naics: ['32513'],
  },
  {
    bea: '325180',
    label: 'Other basic inorganic chemical manufacturing',
    naics: ['32518'],
  },
  {
    bea: '325190',
    label: 'Other basic organic chemical manufacturing',
    naics: ['32519'],
  },
  {
    bea: '325211',
    label: 'Plastics material and resin manufacturing',
    naics: ['325211'],
  },
  {
    bea: '3252A0',
    label:
      'Synthetic rubber and artificial and synthetic fibers and filaments manufacturing',
    naics: ['325212', '32522'],
  },
  { bea: '325310', label: 'Fertilizer manufacturing', naics: ['32531'] },
  {
    bea: '325320',
    label: 'Pesticide and other agricultural chemical manufacturing',
    naics: ['32532'],
  },
  {
    bea: '325411',
    label: 'Medicinal and botanical manufacturing',
    naics: ['325411'],
  },
  {
    bea: '325412',
    label: 'Pharmaceutical preparation manufacturing',
    naics: ['325412'],
  },
  {
    bea: '325413',
    label: 'In-vitro diagnostic substance manufacturing',
    naics: ['325413'],
  },
  {
    bea: '325414',
    label: 'Biological product (except diagnostic) manufacturing',
    naics: ['325414'],
  },
  { bea: '325510', label: 'Paint and coating manufacturing', naics: ['32551'] },
  { bea: '325520', label: 'Adhesive manufacturing', naics: ['32552'] },
  {
    bea: '325610',
    label: 'Soap and cleaning compound manufacturing',
    naics: ['32561'],
  },
  {
    bea: '325620',
    label: 'Toilet preparation manufacturing',
    naics: ['32562'],
  },
  { bea: '325910', label: 'Printing ink manufacturing', naics: ['32591'] },
  {
    bea: '3259A0',
    label: 'All other chemical product and preparation manufacturing',
    naics: ['32592', '32599'],
  },
  {
    bea: '326110',
    label:
      'Plastics packaging materials and unlaminated film and sheet manufacturing',
    naics: ['32611'],
  },
  {
    bea: '326120',
    label:
      'Plastics pipe, pipe fitting, and unlaminated profile shape manufacturing',
    naics: ['32612'],
  },
  {
    bea: '326130',
    label:
      'Laminated plastics plate, sheet (except packaging), and shape manufacturing',
    naics: ['32613'],
  },
  {
    bea: '326140',
    label: 'Polystyrene foam product manufacturing',
    naics: ['32614'],
  },
  {
    bea: '326150',
    label: 'Urethane and other foam product (except polystyrene) manufacturing',
    naics: ['32615'],
  },
  { bea: '326160', label: 'Plastics bottle manufacturing', naics: ['32616'] },
  {
    bea: '326190',
    label: 'Other plastics product manufacturing',
    naics: ['32619'],
  },
  { bea: '326210', label: 'Tire manufacturing', naics: ['32621'] },
  {
    bea: '326220',
    label: 'Rubber and plastics hoses and belting manufacturing',
    naics: ['32622'],
  },
  {
    bea: '326290',
    label: 'Other rubber product manufacturing',
    naics: ['32629'],
  },
  {
    bea: '327100',
    label: 'Clay product and refractory manufacturing',
    naics: ['3271'],
  },
  {
    bea: '327200',
    label: 'Glass and glass product manufacturing',
    naics: ['3272'],
  },
  { bea: '327310', label: 'Cement manufacturing', naics: ['32731'] },
  {
    bea: '327320',
    label: 'Ready-mix concrete manufacturing',
    naics: ['32732'],
  },
  {
    bea: '327330',
    label: 'Concrete pipe, brick, and block manufacturing',
    naics: ['32733'],
  },
  {
    bea: '327390',
    label: 'Other concrete product manufacturing',
    naics: ['32739'],
  },
  {
    bea: '327400',
    label: 'Lime and gypsum product manufacturing',
    naics: ['3274'],
  },
  { bea: '327910', label: 'Abrasive product manufacturing', naics: ['32791'] },
  {
    bea: '327991',
    label: 'Cut stone and stone product manufacturing',
    naics: ['327991'],
  },
  {
    bea: '327992',
    label: 'Ground or treated mineral and earth manufacturing',
    naics: ['327992'],
  },
  { bea: '327993', label: 'Mineral wool manufacturing', naics: ['327993'] },
  {
    bea: '327999',
    label: 'Miscellaneous nonmetallic mineral products',
    naics: ['327999'],
  },
  {
    bea: '331110',
    label: 'Iron and steel mills and ferroalloy manufacturing',
    naics: ['3311'],
  },
  {
    bea: '331200',
    label: 'Steel product manufacturing from purchased steel',
    naics: ['3312'],
  },
  {
    bea: '331313',
    label: 'Alumina refining and primary aluminum production',
    naics: ['331313'],
  },
  {
    bea: '33131B',
    label: 'Aluminum product manufacturing from purchased aluminum',
    naics: ['331315', '331318'],
  },
  {
    bea: '331410',
    label: 'Nonferrous metal (except aluminum) smelting and refining',
    naics: ['331410'],
  },
  {
    bea: '331420',
    label: 'Copper rolling, drawing, extruding and alloying',
    naics: ['33142'],
  },
  {
    bea: '331490',
    label:
      'Nonferrous metal (except copper and aluminum) rolling, drawing, extruding and alloying',
    naics: ['33149'],
  },
  { bea: '331510', label: 'Ferrous metal foundries', naics: ['33151'] },
  { bea: '331520', label: 'Nonferrous metal foundries', naics: ['33152'] },
  { bea: '332114', label: 'Custom roll forming', naics: ['332114'] },
  {
    bea: '332119',
    label: 'Metal crown, closure, and other metal stamping (except automotive)',
    naics: ['332119'],
  },
  {
    bea: '33211A',
    label: 'All other forging, stamping, and sintering',
    naics: ['332111', '332112', '332117'],
  },
  {
    bea: '332200',
    label: 'Cutlery and handtool manufacturing',
    naics: ['3322'],
  },
  {
    bea: '332310',
    label: 'Plate work and fabricated structural product manufacturing',
    naics: ['33231'],
  },
  {
    bea: '332320',
    label: 'Ornamental and architectural metal products manufacturing',
    naics: ['33232'],
  },
  {
    bea: '332410',
    label: 'Power boiler and heat exchanger manufacturing',
    naics: ['33241'],
  },
  {
    bea: '332420',
    label: 'Metal tank (heavy gauge) manufacturing',
    naics: ['33242'],
  },
  {
    bea: '332430',
    label:
      'Metal can, box, and other metal container (light gauge) manufacturing',
    naics: ['33243'],
  },
  { bea: '332500', label: 'Hardware manufacturing', naics: ['3325'] },
  {
    bea: '332600',
    label: 'Spring and wire product manufacturing',
    naics: ['3326'],
  },
  { bea: '332710', label: 'Machine shops', naics: ['33271'] },
  {
    bea: '332720',
    label: 'Turned product and screw, nut, and bolt manufacturing',
    naics: ['33272'],
  },
  {
    bea: '332800',
    label: 'Coating, engraving, heat treating and allied activities',
    naics: ['3328'],
  },
  {
    bea: '332913',
    label: 'Plumbing fixture fitting and trim manufacturing',
    naics: ['332913'],
  },
  {
    bea: '33291A',
    label: 'Valve and fittings other than plumbing',
    naics: ['332911', '332912', '332919'],
  },
  {
    bea: '332991',
    label: 'Ball and roller bearing manufacturing',
    naics: ['332991'],
  },
  {
    bea: '332996',
    label: 'Fabricated pipe and pipe fitting manufacturing',
    naics: ['332996'],
  },
  {
    bea: '332999',
    label: 'Other fabricated metal manufacturing',
    naics: ['332999'],
  },
  {
    bea: '33299A',
    label: 'Ammunition, arms, ordnance, and accessories manufacturing',
    naics: ['332992', '332993', '332994'],
  },
  {
    bea: '333111',
    label: 'Farm machinery and equipment manufacturing',
    naics: ['333111'],
  },
  {
    bea: '333112',
    label: 'Lawn and garden equipment manufacturing',
    naics: ['333112'],
  },
  {
    bea: '333120',
    label: 'Construction machinery manufacturing',
    naics: ['33312'],
  },
  {
    bea: '333130',
    label: 'Mining and oil and gas field machinery manufacturing',
    naics: ['33313'],
  },
  {
    bea: '333242',
    label: 'Semiconductor machinery manufacturing',
    naics: ['333242'],
  },
  {
    bea: '33329A',
    label: 'Other industrial machinery manufacturing',
    naics: ['333241', '333243', '333244', '333249'],
  },
  {
    bea: '333314',
    label: 'Optical instrument and lens manufacturing',
    naics: ['333314'],
  },
  {
    bea: '333316',
    label: 'Photographic and photocopying equipment manufacturing',
    naics: ['333316'],
  },
  {
    bea: '333318',
    label: 'Other commercial and service industry machinery manufacturing',
    naics: ['333318'],
  },
  {
    bea: '333413',
    label:
      'Industrial and commercial fan and blower and air purification equipment manufacturing',
    naics: ['333413'],
  },
  {
    bea: '333414',
    label: 'Heating equipment (except warm air furnaces) manufacturing',
    naics: ['333414'],
  },
  {
    bea: '333415',
    label:
      'Air conditioning, refrigeration, and warm air heating equipment manufacturing',
    naics: ['333415'],
  },
  { bea: '333511', label: 'Industrial mold manufacturing', naics: ['333511'] },
  {
    bea: '333514',
    label: 'Special tool, die, jig, and fixture manufacturing',
    naics: ['333514'],
  },
  { bea: '333517', label: 'Machine tool manufacturing', naics: ['333517'] },
  {
    bea: '33351B',
    label:
      'Cutting and machine tool accessory, rolling mill, and other metalworking machinery manufacturing',
    naics: ['333515', '333519'],
  },
  {
    bea: '333611',
    label: 'Turbine and turbine generator set units manufacturing',
    naics: ['333611'],
  },
  {
    bea: '333612',
    label: 'Speed changer, industrial high-speed drive, and gear manufacturing',
    naics: ['333612'],
  },
  {
    bea: '333613',
    label: 'Mechanical power transmission equipment manufacturing',
    naics: ['333613'],
  },
  {
    bea: '333618',
    label: 'Other engine equipment manufacturing',
    naics: ['333618'],
  },
  {
    bea: '333912',
    label: 'Air and gas compressor manufacturing',
    naics: ['333912'],
  },
  {
    bea: '333914',
    label: 'Measuring, dispensing, and other pumping equipment manufacturing',
    naics: ['333914'],
  },
  {
    bea: '333920',
    label: 'Material handling equipment manufacturing',
    naics: ['33392'],
  },
  {
    bea: '333991',
    label: 'Power-driven handtool manufacturing',
    naics: ['333991'],
  },
  {
    bea: '333993',
    label: 'Packaging machinery manufacturing',
    naics: ['333993'],
  },
  {
    bea: '333994',
    label: 'Industrial process furnace and oven manufacturing',
    naics: ['333994'],
  },
  {
    bea: '33399A',
    label: 'Other general purpose machinery manufacturing',
    naics: ['333992', '333997', '333999'],
  },
  {
    bea: '33399B',
    label: 'Fluid power process machinery',
    naics: ['333995', '333996'],
  },
  {
    bea: '334111',
    label: 'Electronic computer manufacturing',
    naics: ['334111'],
  },
  {
    bea: '334112',
    label: 'Computer storage device manufacturing',
    naics: ['334112'],
  },
  {
    bea: '334118',
    label:
      'Computer terminals and other computer peripheral equipment manufacturing',
    naics: ['334118'],
  },
  {
    bea: '334210',
    label: 'Telephone apparatus manufacturing',
    naics: ['33421'],
  },
  {
    bea: '334220',
    label: 'Broadcast and wireless communications equipment',
    naics: ['33422'],
  },
  {
    bea: '334290',
    label: 'Other communications equipment manufacturing',
    naics: ['33429'],
  },
  {
    bea: '334300',
    label: 'Audio and video equipment manufacturing',
    naics: ['3343'],
  },
  {
    bea: '334413',
    label: 'Semiconductor and related device manufacturing',
    naics: ['334413'],
  },
  {
    bea: '334418',
    label: 'Printed circuit assembly (electronic assembly) manufacturing',
    naics: ['334418'],
  },
  {
    bea: '33441A',
    label: 'Other electronic component manufacturing',
    naics: ['334412', '334416', '334417', '334419'],
  },
  {
    bea: '334510',
    label: 'Electromedical and electrotherapeutic apparatus manufacturing',
    naics: ['334510'],
  },
  {
    bea: '334511',
    label: 'Search, detection, and navigation instruments manufacturing',
    naics: ['334511'],
  },
  {
    bea: '334512',
    label: 'Automatic environmental control manufacturing',
    naics: ['334512'],
  },
  {
    bea: '334513',
    label: 'Industrial process variable instruments manufacturing',
    naics: ['334513'],
  },
  {
    bea: '334514',
    label: 'Totalizing fluid meter and counting device manufacturing',
    naics: ['334514'],
  },
  {
    bea: '334515',
    label: 'Electricity and signal testing instruments manufacturing',
    naics: ['334515'],
  },
  {
    bea: '334516',
    label: 'Analytical laboratory instrument manufacturing',
    naics: ['334516'],
  },
  {
    bea: '334517',
    label: 'Irradiation apparatus manufacturing',
    naics: ['334517'],
  },
  {
    bea: '33451A',
    label:
      'Watch, clock, and other measuring and controlling device manufacturing',
    naics: ['334519'],
  },
  {
    bea: '334610',
    label: 'Manufacturing and reproducing magnetic and optical media',
    naics: ['33461'],
  },
  {
    bea: '335110',
    label: 'Electric lamp bulb and part manufacturing',
    naics: ['33511'],
  },
  { bea: '335120', label: 'Lighting fixture manufacturing', naics: ['33512'] },
  {
    bea: '335210',
    label: 'Small electrical appliance manufacturing',
    naics: ['33521'],
  },
  {
    bea: '335220',
    label: 'Major household appliance manufacturing',
    naics: ['335220'],
  },
  {
    bea: '335311',
    label: 'Power, distribution, and specialty transformer manufacturing',
    naics: ['335311'],
  },
  {
    bea: '335312',
    label: 'Motor and generator manufacturing',
    naics: ['335312'],
  },
  {
    bea: '335313',
    label: 'Switchgear and switchboard apparatus manufacturing',
    naics: ['335313'],
  },
  {
    bea: '335314',
    label: 'Relay and industrial control manufacturing',
    naics: ['335314'],
  },
  { bea: '335911', label: 'Storage battery manufacturing', naics: ['335911'] },
  { bea: '335912', label: 'Primary battery manufacturing', naics: ['335912'] },
  {
    bea: '335920',
    label: 'Communication and energy wire and cable manufacturing',
    naics: ['33592'],
  },
  { bea: '335930', label: 'Wiring device manufacturing', naics: ['33593'] },
  {
    bea: '335991',
    label: 'Carbon and graphite product manufacturing',
    naics: ['335991'],
  },
  {
    bea: '335999',
    label:
      'All other miscellaneous electrical equipment and component manufacturing',
    naics: ['335999'],
  },
  { bea: '336111', label: 'Automobile manufacturing', naics: ['336111'] },
  {
    bea: '336112',
    label: 'Light truck and utility vehicle manufacturing',
    naics: ['336112'],
  },
  { bea: '336120', label: 'Heavy duty truck manufacturing', naics: ['33612'] },
  {
    bea: '336211',
    label: 'Motor vehicle body manufacturing',
    naics: ['336211'],
  },
  { bea: '336212', label: 'Truck trailer manufacturing', naics: ['336212'] },
  { bea: '336213', label: 'Motor home manufacturing', naics: ['336213'] },
  {
    bea: '336214',
    label: 'Travel trailer and camper manufacturing',
    naics: ['336214'],
  },
  {
    bea: '336310',
    label: 'Motor vehicle gasoline engine and engine parts manufacturing',
    naics: ['33631'],
  },
  {
    bea: '336320',
    label: 'Motor vehicle electrical and electronic equipment manufacturing',
    naics: ['33632'],
  },
  {
    bea: '336350',
    label: 'Motor vehicle transmission and power train parts manufacturing',
    naics: ['33635'],
  },
  {
    bea: '336360',
    label: 'Motor vehicle seating and interior trim manufacturing',
    naics: ['33636'],
  },
  { bea: '336370', label: 'Motor vehicle metal stamping', naics: ['33637'] },
  {
    bea: '336390',
    label: 'Other motor vehicle parts manufacturing',
    naics: ['33639'],
  },
  {
    bea: '3363A0',
    label:
      'Motor vehicle steering, suspension component (except spring), and brake systems manufacturing',
    naics: ['33633', '33634'],
  },
  { bea: '336411', label: 'Aircraft manufacturing', naics: ['336411'] },
  {
    bea: '336412',
    label: 'Aircraft engine and engine parts manufacturing',
    naics: ['336412'],
  },
  {
    bea: '336413',
    label: 'Other aircraft parts and auxiliary equipment manufacturing',
    naics: ['336413'],
  },
  {
    bea: '336414',
    label: 'Guided missile and space vehicle manufacturing',
    naics: ['336414'],
  },
  {
    bea: '33641A',
    label: 'Propulsion units and parts for space vehicles and guided missiles',
    naics: ['336415', '336419'],
  },
  {
    bea: '336500',
    label: 'Railroad rolling stock manufacturing',
    naics: ['3365'],
  },
  { bea: '336611', label: 'Ship building and repairing', naics: ['336611'] },
  { bea: '336612', label: 'Boat building', naics: ['336612'] },
  {
    bea: '336991',
    label: 'Motorcycle, bicycle, and parts manufacturing',
    naics: ['336991'],
  },
  {
    bea: '336992',
    label: 'Military armored vehicle, tank, and tank component manufacturing',
    naics: ['336992'],
  },
  {
    bea: '336999',
    label: 'All other transportation equipment manufacturing',
    naics: ['336999'],
  },
  {
    bea: '337110',
    label: 'Wood kitchen cabinet and countertop manufacturing',
    naics: ['33711'],
  },
  {
    bea: '337121',
    label: 'Upholstered household furniture manufacturing',
    naics: ['337121'],
  },
  {
    bea: '337122',
    label: 'Nonupholstered wood household furniture manufacturing',
    naics: ['337122'],
  },
  {
    bea: '337127',
    label: 'Institutional furniture manufacturing',
    naics: ['337127'],
  },
  {
    bea: '33712N',
    label: 'Other household nonupholstered furniture',
    naics: ['337124', '337125'],
  },
  {
    bea: '337215',
    label: 'Showcase, partition, shelving, and locker manufacturing',
    naics: ['337215'],
  },
  {
    bea: '33721A',
    label:
      'Office furniture and custom architectural woodwork and millwork manufacturing',
    naics: ['337211', '337212', '337214'],
  },
  {
    bea: '337900',
    label: 'Other furniture related product manufacturing',
    naics: ['3379'],
  },
  {
    bea: '339112',
    label: 'Surgical and medical instrument manufacturing',
    naics: ['339112'],
  },
  {
    bea: '339113',
    label: 'Surgical appliance and supplies manufacturing',
    naics: ['339113'],
  },
  {
    bea: '339114',
    label: 'Dental equipment and supplies manufacturing',
    naics: ['339114'],
  },
  { bea: '339115', label: 'Ophthalmic goods manufacturing', naics: ['339115'] },
  { bea: '339116', label: 'Dental laboratories', naics: ['339116'] },
  {
    bea: '339910',
    label: 'Jewelry and silverware manufacturing',
    naics: ['33991'],
  },
  {
    bea: '339920',
    label: 'Sporting and athletic goods manufacturing',
    naics: ['33992'],
  },
  {
    bea: '339930',
    label: 'Doll, toy, and game manufacturing',
    naics: ['33993'],
  },
  {
    bea: '339940',
    label: 'Office supplies (except paper) manufacturing',
    naics: ['33994'],
  },
  { bea: '339950', label: 'Sign manufacturing', naics: ['33995'] },
  {
    bea: '339990',
    label: 'All other miscellaneous manufacturing',
    naics: ['33999'],
  },
  {
    bea: '423100',
    label: 'Motor vehicle and motor vehicle parts and supplies',
    naics: ['4231'],
  },
  {
    bea: '423400',
    label: 'Professional and commercial equipment and supplies',
    naics: ['4234'],
  },
  {
    bea: '423600',
    label: 'Household appliances and electrical and electronic goods',
    naics: ['4236'],
  },
  {
    bea: '423800',
    label: 'Machinery, equipment, and supplies',
    naics: ['4238'],
  },
  {
    bea: '423A00',
    label: 'Other durable goods merchant wholesalers',
    naics: ['4232', '4233', '4235', '4237', '4239'],
  },
  { bea: '424200', label: "Drugs and druggists' sundries", naics: ['4242'] },
  {
    bea: '424400',
    label: 'Grocery and related product wholesalers',
    naics: ['4244'],
  },
  { bea: '424700', label: 'Petroleum and petroleum products', naics: ['4247'] },
  {
    bea: '424A00',
    label: 'Other nondurable goods merchant wholesalers',
    naics: ['4241', '4243', '4245', '4246', '4248', '4249'],
  },
  {
    bea: '425000',
    label: 'Wholesale electronic markets and agents and brokers',
    naics: ['425'],
  },
  { bea: '441000', label: 'Motor vehicle and parts dealers', naics: ['441'] },
  {
    bea: '444000',
    label: 'Building material and garden equipment and supplies dealers',
    naics: ['444'],
  },
  { bea: '445000', label: 'Food and beverage stores', naics: ['445'] },
  { bea: '446000', label: 'Health and personal care stores', naics: ['446'] },
  { bea: '447000', label: 'Gasoline stations', naics: ['447'] },
  {
    bea: '448000',
    label: 'Clothing and clothing accessories stores',
    naics: ['448'],
  },
  { bea: '452000', label: 'General merchandise stores', naics: ['452'] },
  { bea: '454000', label: 'Nonstore retailers', naics: ['454'] },
  { bea: '481000', label: 'Air transportation', naics: ['481'] },
  { bea: '482000', label: 'Rail transportation', naics: ['482'] },
  { bea: '483000', label: 'Water transportation', naics: ['483'] },
  { bea: '484000', label: 'Truck transportation', naics: ['484'] },
  {
    bea: '485000',
    label: 'Transit and ground passenger transportation',
    naics: ['485'],
  },
  { bea: '486000', label: 'Pipeline transportation', naics: ['486'] },
  {
    bea: '48A000',
    label: 'Scenic and sightseeing transportation and support activities',
    naics: ['487', '488'],
  },
  { bea: '491000', label: 'Postal service', naics: ['491'] },
  { bea: '492000', label: 'Couriers and messengers', naics: ['492'] },
  { bea: '493000', label: 'Warehousing and storage', naics: ['493'] },
  {
    bea: '4B0000',
    label: 'All other retail',
    naics: ['442', '443', '451', '453'],
  },
  { bea: '511110', label: 'Newspaper publishers', naics: ['51111'] },
  { bea: '511120', label: 'Periodical publishers', naics: ['51112'] },
  { bea: '511130', label: 'Book publishers', naics: ['51113'] },
  {
    bea: '5111A0',
    label: 'Directory, mailing list, and other publishers',
    naics: ['51114', '51119'],
  },
  { bea: '511200', label: 'Software publishers', naics: ['51121'] },
  {
    bea: '512100',
    label: 'Motion picture and video industries',
    naics: ['5121'],
  },
  { bea: '512200', label: 'Sound recording industries', naics: ['5122'] },
  {
    bea: '515100',
    label: 'Radio and television broadcasting',
    naics: ['5151'],
  },
  {
    bea: '515200',
    label: 'Cable and other subscription programming',
    naics: ['5152'],
  },
  {
    bea: '517110',
    label: 'Wired telecommunications carriers',
    naics: ['517311'],
  },
  {
    bea: '517210',
    label: 'Wireless telecommunications carriers (except satellite)',
    naics: ['517312'],
  },
  {
    bea: '517A00',
    label:
      'Satellite, telecommunications resellers, and all other telecommunications',
    naics: ['5174', '5175', '5176', '5177', '5178', '5179'],
  },
  {
    bea: '518200',
    label: 'Data processing, hosting, and related services',
    naics: ['5182'],
  },
  {
    bea: '519130',
    label: 'Internet publishing and broadcasting and Web search portals',
    naics: ['51913'],
  },
  {
    bea: '5191A0',
    label:
      'News syndicates, libraries, archives and all other information services',
    naics: ['51911', '51912', '51919'],
  },
  {
    bea: '522A00',
    label: 'Nondepository credit intermediation and related activities',
    naics: ['5222', '5223'],
  },
  {
    bea: '523900',
    label: 'Other financial investment activities',
    naics: ['5239'],
  },
  {
    bea: '523A00',
    label: 'Securities and commodity contracts intermediation and brokerage',
    naics: ['5231', '5232'],
  },
  { bea: '524113', label: 'Direct life insurance carriers', naics: ['524113'] },
  {
    bea: '5241XX',
    label: 'Insurance carriers, except direct life',
    naics: ['524114', '52412', '52413'],
  },
  {
    bea: '524200',
    label: 'Insurance agencies, brokerages, and related activities',
    naics: ['5242'],
  },
  {
    bea: '525000',
    label: 'Funds, trusts, and other financial vehicles',
    naics: ['525'],
  },
  {
    bea: '52A000',
    label: 'Monetary authorities and depository credit intermediation',
    naics: ['521', '5221'],
  },
  { bea: '531HSO', label: 'Owner-occupied housing', naics: [] },
  { bea: '531HST', label: 'Tenant-occupied housing', naics: ['531'] },
  { bea: '531ORE', label: 'Other real estate', naics: ['531'] },
  {
    bea: '532100',
    label: 'Automotive equipment rental and leasing',
    naics: ['5321'],
  },
  {
    bea: '532400',
    label:
      'Commercial and industrial machinery and equipment rental and leasing',
    naics: ['5324'],
  },
  {
    bea: '532A00',
    label: 'General and consumer goods rental',
    naics: ['5322', '5323'],
  },
  {
    bea: '533000',
    label: 'Lessors of nonfinancial intangible assets',
    naics: ['533'],
  },
  { bea: '541100', label: 'Legal services', naics: ['5411'] },
  {
    bea: '541200',
    label: 'Accounting, tax preparation, bookkeeping, and payroll services',
    naics: ['5412'],
  },
  {
    bea: '541300',
    label: 'Architectural, engineering, and related services',
    naics: ['5413'],
  },
  { bea: '541400', label: 'Specialized design services', naics: ['5414'] },
  {
    bea: '541511',
    label: 'Custom computer programming services',
    naics: ['541511'],
  },
  {
    bea: '541512',
    label: 'Computer systems design services',
    naics: ['541512'],
  },
  {
    bea: '54151A',
    label: 'Other computer related services, including facilities management',
    naics: ['541513', '541519'],
  },
  { bea: '541610', label: 'Management consulting services', naics: ['54161'] },
  {
    bea: '5416A0',
    label: 'Environmental and other technical consulting services',
    naics: ['54162', '54169'],
  },
  {
    bea: '541700',
    label: 'Scientific research and development services',
    naics: ['5417'],
  },
  {
    bea: '541800',
    label: 'Advertising, public relations, and related services',
    naics: ['5418'],
  },
  { bea: '541920', label: 'Photographic services', naics: ['54192'] },
  { bea: '541940', label: 'Veterinary services', naics: ['54194'] },
  {
    bea: '5419A0',
    label:
      'All other miscellaneous professional, scientific, and technical services',
    naics: ['54191', '54193', '54199'],
  },
  {
    bea: '550000',
    label: 'Management of companies and enterprises',
    naics: ['55'],
  },
  { bea: '561100', label: 'Office administrative services', naics: ['5611'] },
  { bea: '561200', label: 'Facilities support services', naics: ['5612'] },
  { bea: '561300', label: 'Employment services', naics: ['5613'] },
  { bea: '561400', label: 'Business support services', naics: ['5614'] },
  {
    bea: '561500',
    label: 'Travel arrangement and reservation services',
    naics: ['5615'],
  },
  {
    bea: '561600',
    label: 'Investigation and security services',
    naics: ['5616'],
  },
  {
    bea: '561700',
    label: 'Services to buildings and dwellings',
    naics: ['5617'],
  },
  { bea: '561900', label: 'Other support services', naics: ['5619'] },
  {
    bea: '562000',
    label: 'Waste management and remediation services',
    naics: ['562'],
  },
  { bea: '611100', label: 'Elementary and secondary schools', naics: ['6111'] },
  {
    bea: '611A00',
    label: 'Junior colleges, colleges, universities, and professional schools',
    naics: ['6112', '6113'],
  },
  {
    bea: '611B00',
    label: 'Other educational services',
    naics: ['6114', '6115', '6116', '6117'],
  },
  { bea: '621100', label: 'Offices of physicians', naics: ['6211'] },
  { bea: '621200', label: 'Offices of dentists', naics: ['6212'] },
  {
    bea: '621300',
    label: 'Offices of other health practitioners',
    naics: ['6213'],
  },
  { bea: '621400', label: 'Outpatient care centers', naics: ['6214'] },
  {
    bea: '621500',
    label: 'Medical and diagnostic laboratories',
    naics: ['6215'],
  },
  { bea: '621600', label: 'Home health care services', naics: ['6216'] },
  {
    bea: '621900',
    label: 'Other ambulatory health care services',
    naics: ['6219'],
  },
  { bea: '622000', label: 'Hospitals', naics: ['622'] },
  {
    bea: '623A00',
    label: 'Nursing and community care facilities',
    naics: ['6231', '6233'],
  },
  {
    bea: '623B00',
    label:
      'Residential mental health, substance abuse, and other residential care facilities',
    naics: ['6232', '6239'],
  },
  { bea: '624100', label: 'Individual and family services', naics: ['6241'] },
  { bea: '624400', label: 'Child day care services', naics: ['6244'] },
  {
    bea: '624A00',
    label:
      'Community food, housing, and other relief services, including vocational rehabilitation services',
    naics: ['6242', '6243'],
  },
  { bea: '711100', label: 'Performing arts companies', naics: ['7111'] },
  { bea: '711200', label: 'Spectator sports', naics: ['7112'] },
  {
    bea: '711500',
    label: 'Independent artists, writers, and performers',
    naics: ['7115'],
  },
  {
    bea: '711A00',
    label:
      'Promoters of performing arts and sports and agents for public figures',
    naics: ['7113', '7114'],
  },
  {
    bea: '712000',
    label: 'Museums, historical sites, zoos, and parks',
    naics: ['712'],
  },
  { bea: '713100', label: 'Amusement parks and arcades', naics: ['7131'] },
  {
    bea: '713200',
    label: 'Gambling industries (except casino hotels)',
    naics: ['7132'],
  },
  {
    bea: '713900',
    label: 'Other amusement and recreation industries',
    naics: ['7139'],
  },
  { bea: '721000', label: 'Accommodation', naics: ['721'] },
  { bea: '722110', label: 'Full-service restaurants', naics: ['722511'] },
  { bea: '722211', label: 'Limited-service restaurants', naics: ['722513'] },
  {
    bea: '722A00',
    label: 'All other food and drinking places',
    naics: ['7223', '7224', '722514', '722515'],
  },
  {
    bea: '811100',
    label: 'Automotive repair and maintenance (including car washes)',
    naics: ['8111'],
  },
  {
    bea: '811200',
    label: 'Electronic and precision equipment repair and maintenance',
    naics: ['8112'],
  },
  {
    bea: '811300',
    label:
      'Commercial and industrial machinery and equipment repair and maintenance',
    naics: ['8113'],
  },
  {
    bea: '811400',
    label: 'Personal and household goods repair and maintenance',
    naics: ['8114'],
  },
  { bea: '812100', label: 'Personal care services', naics: ['8121'] },
  { bea: '812200', label: 'Death care services', naics: ['8122'] },
  {
    bea: '812300',
    label: 'Dry-cleaning and laundry services',
    naics: ['8123'],
  },
  { bea: '812900', label: 'Other personal services', naics: ['8129'] },
  { bea: '813100', label: 'Religious organizations', naics: ['8131'] },
  {
    bea: '813A00',
    label: 'Grantmaking, giving, and social advocacy organizations',
    naics: ['8132', '8133'],
  },
  {
    bea: '813B00',
    label: 'Civic, social, professional, and similar organizations',
    naics: ['8134', '8139'],
  },
  {
    bea: 'GSLGE',
    label: 'State and local government (educational services)',
    naics: [],
  },
  {
    bea: 'GSLGH',
    label: 'State and local government (hospitals and health services)',
    naics: [],
  },
  {
    bea: 'GSLGO',
    label: 'State and local government (other services)',
    naics: [],
  },
  { bea: 'S00102', label: 'Other federal government enterprises', naics: [] },
  {
    bea: 'S00203',
    label: 'Other state and local government enterprises',
    naics: [],
  },
  { bea: 'S00500', label: 'Federal general government (defense)', naics: [] },
  {
    bea: 'S00600',
    label: 'Federal general government (nondefense)',
    naics: [],
  },
];

const BEA_LABEL_BY_CODE: ReadonlyMap<string, string> = new Map(
  BEA_INDUSTRIES.map((industry) => [industry.bea, industry.label])
);

// The impact response returns BEA codes; resolve to the human title (falling
// back to the raw code so an unknown industry still renders a stable label).
export const beaLabel = (code: string): string =>
  BEA_LABEL_BY_CODE.get(code) ?? `BEA ${code}`;

// ---------------------------------------------------------------------
// NAICS sector grouping for the industry picker. The 396 detail industries are
// already in sector order (their codes carry a NAICS-aligned 2-digit prefix);
// grouping turns the flat list into the 20 scannable NAICS sectors. Derived
// from the BEA code prefix — government codes (G*/S*) collapse into one group.
// ---------------------------------------------------------------------

const GOVERNMENT = 'Government';

const SECTOR_BY_PREFIX: Record<string, string> = {
  '11': 'Agriculture, Forestry, Fishing and Hunting',
  '21': 'Mining, Quarrying, and Oil and Gas Extraction',
  '22': 'Utilities',
  '23': 'Construction',
  '31': 'Manufacturing',
  '32': 'Manufacturing',
  '33': 'Manufacturing',
  '42': 'Wholesale Trade',
  '44': 'Retail Trade',
  '45': 'Retail Trade',
  '4B': 'Retail Trade',
  '48': 'Transportation and Warehousing',
  '49': 'Transportation and Warehousing',
  '51': 'Information',
  '52': 'Finance and Insurance',
  '53': 'Real Estate and Rental and Leasing',
  '54': 'Professional, Scientific, and Technical Services',
  '55': 'Management of Companies and Enterprises',
  '56': 'Administrative and Support and Waste Management and Remediation Services',
  '61': 'Educational Services',
  '62': 'Health Care and Social Assistance',
  '71': 'Arts, Entertainment, and Recreation',
  '72': 'Accommodation and Food Services',
  '81': 'Other Services (except Public Administration)',
};

// Canonical sector order for the grouped picker (the order the economy walks
// in the BEA code space). The picker renders groups in this order and hides
// any that filter to empty.
export const SECTORS_IN_ORDER: readonly string[] = [
  'Agriculture, Forestry, Fishing and Hunting',
  'Mining, Quarrying, and Oil and Gas Extraction',
  'Utilities',
  'Construction',
  'Manufacturing',
  'Wholesale Trade',
  'Retail Trade',
  'Transportation and Warehousing',
  'Information',
  'Finance and Insurance',
  'Real Estate and Rental and Leasing',
  'Professional, Scientific, and Technical Services',
  'Management of Companies and Enterprises',
  'Administrative and Support and Waste Management and Remediation Services',
  'Educational Services',
  'Health Care and Social Assistance',
  'Arts, Entertainment, and Recreation',
  'Accommodation and Food Services',
  'Other Services (except Public Administration)',
  GOVERNMENT,
];

// Map a BEA detail code to its NAICS sector. Every code in BEA_INDUSTRIES
// resolves to one of SECTORS_IN_ORDER; the GOVERNMENT fallback also catches any
// unexpected code so an option is never silently dropped from the picker.
export const sectorForBea = (bea: string): string => {
  if (bea.startsWith('G') || bea.startsWith('S')) return GOVERNMENT;
  return SECTOR_BY_PREFIX[bea.slice(0, 2)] ?? GOVERNMENT;
};
